import React, { Component } from "react";
import { connect } from "react-redux";
import AuthHOC from "./Auth";
import PropTypes from "prop-types";
import { Button } from "reactstrap";
import {
  Alert,
  Stack,
  Typography,
  Box,
  Paper,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Divider,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  Chip,
} from "@mui/material";
import LanguageIcon from "@mui/icons-material/Language";
import SupportAgentIcon from "@mui/icons-material/SupportAgent";
import ForumIcon from "@mui/icons-material/Forum";
import KeyIcon from "@mui/icons-material/Key";
import Header from "../components/Header";
import { Link } from "react-router-dom";
import moment from "moment";

// Event timestamps arrive from the API as ISO-8601 UTC strings (e.g. "2026-09-18T10:41:48.914Z").
// Format them in the browser's local time with the same pattern Event Viewer uses (ConsoleView.js),
// so the two pages agree. Empty values stay blank instead of rendering "Invalid date".
const formatLocalDateTime = (value) => (value ? moment(value).format("HH:mm:ss DD/MM/YYYY") : "");

class DashboardView extends Component {
  constructor(props) {
    super(props);
    this.state = {
      loading: false,
      sidebarOpen: true,
      toasts: [],
      status: "",
      actionType: "",
      importype: "",
      fromDate: "",
      toDate: "",
      reference: "",
      page_number: 1,
      logs: [],
      folder: "",
      pollers: [],
      error: null,
      successMsg: '',
      ... props
    };
  }

  componentDidMount() {
    this.getActionEntries(10);
    this.getPollerDirectories(5);

    if (localStorage.getItem('licenseActivated')) {
      localStorage.removeItem('licenseActivated');
      this.setState({ successMsg: 'Your licence has been successfully activated.' });
      this._successTimer = setTimeout(() => {
        this.setState({ successMsg: '' });
      }, 10000);
    }
  }

  componentWillUnmount() {
    if (this._successTimer) {
      clearTimeout(this._successTimer);
    }
  }

  setLoading = (loading) => this.setState({ loading });

  statusChipColor = (status) => {
    const s = String(status).toLowerCase();
    if (["success", "ok", "completed", "complete", "active"].includes(s)) return "success";
    if (["pending", "inprogress", "processing"].includes(s)) return "warning";
    if (["failed", "error"].includes(s)) return "error";
    return "default";
  };

  hasWidgetAccess = (key) => {
    const vp =
      this.props?.userReducer?.viewpermissions ||
      {};

    if (!vp || typeof vp !== "object") return false;

    const item = vp[key];
    if (!item) return false;
    if (typeof item === "object" && Object.keys(item).length > 0) return true;

    if (typeof item === "boolean") return item === true;

    return false;
  };


  PermissionGate = ({ perm, fallback = null, children }) => {
    const allowed = this.hasWidgetAccess(perm);
    if (!allowed) {
      return (
        fallback || (
          <div className="no-permission">
            You do not have permission to view this widget, please speak to your administrator.
          </div>
        )
      );
    }
    return children;
  };



  getActionEntries = (limit = null) => {
    this.setLoading(true);

    const q = new URLSearchParams({
      status: this.state.status || "",
      type: this.state.actionType || "",
      import: this.state.importype || "",
      fromdate: this.state.fromDate || "",
      todate: this.state.toDate || "",
      reference: this.state.reference || "",
      page: String(this.state.page_number || 1),
      limit: "15",
    });

    const url = `${window.location.protocol}//${window.location.host}/api/actions/get_all?${q.toString()}`;

    fetch(url, {
      method: "GET",
      headers: { "Content-Type": "application/json" },
    })
      .then((res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return res.json();
      })
      .then((result) => {
        let logs = result;
        if (Array.isArray(result) && limit !== null) {
          logs = result.slice(0, limit);
        }
        this.setState({ logs, error: null });
      })
      .catch((error) => {
        this.setState({ error: error?.message || "Failed to load data." });
      })
      .finally(() => this.setLoading(false));
  };

  renderActionsTable() {
    const { logs, loading, error } = this.state;

    if (loading) return <div className="muted-text">Loading...</div>;
    if (error) return <div className="error-text">Error: {error}</div>;
    if (!logs || logs.length === 0) return <div className="muted-text">No recent actions.</div>;

    const headerMap = [
      { key: "datetime", label: "Date" },
      { key: "type", label: "Type" },
      { key: "status", label: "Status" },
      { key: "reference", label: "Reference" },
      { key: "message", label: "Message" },
    ];

    const pick = (obj, candidates, fallback = "") => {
      for (const c of candidates) {
        if (obj && obj[c] !== undefined && obj[c] !== null) return obj[c];
      }
      return fallback;
    };

    const normalize = (row) => ({
      datetime: pick(row, ["datetime", "createdAt", "timestamp", "time"]) || "",
      type: pick(row, ["type", "actionType"], ""),
      status: pick(row, ["status", "state"], ""),
      reference: pick(row, ["reference", "ref", "id"], ""),
      message: pick(row, ["message", "msg", "description"], ""),
    });

    const rows = logs.map(normalize);

    return (
      <TableContainer component={Paper} elevation={0} sx={{ border: "1px solid #eee", borderRadius: "12px" }}>
        <Table size="small">
          <TableHead sx={{ backgroundColor: "#f5f5f5" }}>
            <TableRow>
              {headerMap.map((h) => (
                <TableCell key={h.key}>{h.label}</TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((r, idx) => (
              <TableRow key={idx}>
                <TableCell>{formatLocalDateTime(r.datetime)}</TableCell>
                <TableCell>{r.type}</TableCell>
                <TableCell>
                  <Chip label={r.status || "-"} color={this.statusChipColor(r.status)} size="small" />
                </TableCell>
                <TableCell sx={{ fontFamily: "monospace" }}>{r.reference}</TableCell>
                <TableCell sx={{ maxWidth: 420, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {r.message}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    );
  }

  renderPollersTable() {
    const {pollers, loading, error} = this.state;
    if (loading) return <div className="muted-text">Loading...</div>;
    if (error) return <div className="error-text">Error: {error}</div>;
    if (!pollers || pollers.length === 0) return <div className="muted-text">No recent actions.</div>;

    const headerMap = [
      { key: "datetime", label: "Date" },
      { key: "description", label: "Description" },
      { key: "directory", label: "Directory" },
      { key: "documentType", label: "Type" },
      { key: "folder", label: "Folder" },
      { key: "status", label: "Status" },
    ]

    const pick = (obj, candidates, fallback = "") => {
      for (const c of candidates) {
        if (obj && obj[c] !== undefined && obj[c] !== null) return obj[c];
      }
      return fallback;
    };

    const normalize = (row) => ({
      datetime: pick(row, ["datetime", "createdAt", "timestamp", "time"]) || "",
      description: pick(row, ["description"], ""),
      directory: pick(row, ["directory"], ""),
      documentType: pick(row, ["documentType"], ""),
      folder: pick(row, ["folder"], ""),
      pollerStatus: pick(row, ["status"], ""),
    });

    const rows = pollers.map(normalize);

    return (
      <TableContainer component={Paper} elevation={0} sx={{ border: "1px solid #eee", borderRadius: "12px" }}>
        <Table size="small">
          <TableHead sx={{ backgroundColor: "#f5f5f5" }}>
            <TableRow>
              {headerMap.map((h) => (
                <TableCell key={h.key}>{h.label}</TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((r, idx) => (
              <TableRow key={idx}>
                <TableCell>{formatLocalDateTime(r.datetime)}</TableCell>
                <TableCell>{r.description}</TableCell>
                <TableCell sx={{ fontFamily: "monospace" }}>{r.directory}</TableCell>
                <TableCell sx={{ maxWidth: 420, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {r.documentType}
                </TableCell>
                <TableCell sx={{ maxWidth: 420, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {r.folder}
                </TableCell>
                <TableCell>
                  <Chip label={r.pollerStatus || "-"} color={this.statusChipColor(r.pollerStatus)} size="small" />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    );
  }

  getPollerDirectories = (limit = 5) => {
    this.setLoading(true);

    const url = `${window.location.protocol}//${window.location.host}/api/poller/directories`;
    fetch(url, {
      method: "GET",
      headers: {"Content-Type" : "application/json"},
    })
    .then((res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return res.json();
      })
      .then((result) => {
        let pollers = result;
        if (Array.isArray(result) && limit !== null) {
          pollers = result.slice(0, limit);
        }
        this.setState({ pollers, error: null });
      })
      .catch((error) => {
        this.setState({ error: error?.message || "Failed to load data." });
      })
      .finally(() => this.setLoading(false));
  }

  render() {
    const { parsed, localisation } = this.props;

    return (
      <div className="App">
        <Header visible={!parsed?.X3} />
        <div id="s-site-body" className="page-wrapper">
        <div className="s-page">
          <div className="s-page-header">
            <Stack spacing={0.5} alignItems="flex-start" sx={{ mb: 3, textAlign: "left" }}>
              <Typography variant="h5" sx={{ fontWeight: 600 }}>
                {localisation["menu_header_dashboard"]
                  ? localisation["menu_header_dashboard"].element_lang
                  : "Dashboard"}
              </Typography>
              <Typography variant="body1" color="text.secondary">
                Overview of recent activity, pollers, and quick links
              </Typography>
            </Stack>
          </div>

          {this.state.successMsg && (
            <Alert severity="success" sx={{ mb: 2 }}>
              {this.state.successMsg}
            </Alert>
          )}

          <section className="dashboard-layout">
          <Box sx={{ display: "flex", gap: 3, mt: 2, alignItems: "stretch" }}>
          <Box sx={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: "20px" }}>
            <article className="card table-card wide-card">
              <div className="card-header-row">
                <h3 className="card-title">Event viewer / Action entries </h3>
                <div className="card-actions">
                  {this.hasWidgetAccess("event_viewer") && (
                     <Button 
                        size="sm" 
                        outline 
                        className="btn-ghost" 
                        onClick={() => this.getActionEntries(10)}
                        disabled={!this.hasWidgetAccess("event_viewer")}
                        >
                        Refresh
                    </Button>
                  )}
                 {this.hasWidgetAccess("event_viewer") && (
                  <Link to="/site/console">
                    <Button 
                      size="sm" 
                      className="btn-primary-soft"
                      >
                      Open Console
                    </Button>
                  </Link>
                 )}
                </div>
              </div>

              <div className="card-content">
                {this.PermissionGate({
                  perm: "event_viewer",
                  children: this.renderActionsTable(),
                })}
              </div>

              <div className="card-footer">
                <span className="label-text">
                  {this.hasWidgetAccess("event_viewer") && (
                    <div className="label-text">Showing latest activity.</div>
                  )}
                </span>
              </div>
            </article>

            <article className="card table-card wide-card">
              <div className="card-header-row">
                <h3 className="card-title">Directory pollers</h3>
                <div className="card-actions">
                  {this.hasWidgetAccess("polling_service") && (
                    <Button 
                      size="sm" 
                      outline 
                      className="btn-ghost" 
                      onClick={() => this.getPollerDirectories(5)}
                      disabled={!this.hasWidgetAccess("polling_service")}
                      >
                      Refresh
                  </Button>
                  )}
                  {this.hasWidgetAccess("polling_service") && (
                    <Link to="/site/polling_service">
                    <Button 
                      size="sm" 
                      className="btn-primary-soft"
                      disabled={!this.hasWidgetAccess("polling_service")}
                      >
                      Open Poller Services
                    </Button>
                  </Link>
                  )}
                </div>
              </div>

              <div className="card-content">
                {this.PermissionGate({
                  perm: "polling_service",
                  children: this.renderPollersTable(),
                })}
              </div>

              <div className="card-footer">
                <span className="label-text">
                  {this.hasWidgetAccess("polling_service") && (
                    <div className="label-text">Showing latest activity.</div>
                  )}
                </span>
              </div>
            </article>
          </Box>

          {/* Quick Links side panel */}
          <Paper elevation={1} sx={{ width: 260, flexShrink: 0, borderTop: "4px solid #7443f4", order: -1 }}>
            <List component="nav" sx={{ py: 1 }}>
              <ListItem disablePadding>
                <ListItemButton
                  component="a"
                  href="https://datel.info/"
                  target="_blank"
                  rel="noreferrer"
                  sx={{ "&:hover": { backgroundColor: "rgba(116, 67, 244, 0.08)" } }}
                >
                  <ListItemIcon sx={{ minWidth: 40 }}>
                    <LanguageIcon />
                  </ListItemIcon>
                  <ListItemText primary="Official website" primaryTypographyProps={{ fontSize: "0.9rem" }} />
                </ListItemButton>
              </ListItem>
              <ListItem disablePadding>
                <ListItemButton
                  component="a"
                  href="https://datel.info/customer-hub/contact-support"
                  target="_blank"
                  rel="noreferrer"
                  sx={{ "&:hover": { backgroundColor: "rgba(116, 67, 244, 0.08)" } }}
                >
                  <ListItemIcon sx={{ minWidth: 40 }}>
                    <SupportAgentIcon />
                  </ListItemIcon>
                  <ListItemText primary="Datel support" primaryTypographyProps={{ fontSize: "0.9rem" }} />
                </ListItemButton>
              </ListItem>
              <ListItem disablePadding>
                <ListItemButton
                  component="a"
                  href="https://datel.info/customer-hub/speak-to-us"
                  target="_blank"
                  rel="noreferrer"
                  sx={{ "&:hover": { backgroundColor: "rgba(116, 67, 244, 0.08)" } }}
                >
                  <ListItemIcon sx={{ minWidth: 40 }}>
                    <ForumIcon />
                  </ListItemIcon>
                  <ListItemText primary="Speak to us" primaryTypographyProps={{ fontSize: "0.9rem" }} />
                </ListItemButton>
              </ListItem>

              <Divider sx={{ my: 1 }} />

              <ListItem disablePadding>
                <ListItemButton
                  component="a"
                  href="/site/config?tab=license"
                  rel="noreferrer"
                  sx={{ "&:hover": { backgroundColor: "rgba(116, 67, 244, 0.08)" } }}
                >
                  <ListItemIcon sx={{ minWidth: 40 }}>
                    <KeyIcon sx={{ color: "#f9a825" }} />
                  </ListItemIcon>
                  <ListItemText primary="License" primaryTypographyProps={{ fontSize: "0.9rem" }} />
                </ListItemButton>
              </ListItem>
            </List>
          </Paper>
          </Box>
          </section>
        </div>
        </div>
      </div>
    );
  }
}

DashboardView.propTypes = {
  userReducer: PropTypes.object,
  localisation: PropTypes.object,
  parsed: PropTypes.object,
};

export default connect(function (state) {
  return state;
})(AuthHOC(DashboardView, { name: "dashboard" }));
